import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

// Admin editor for service/delivery zones (installation rate, onsite fee, local delivery fee by area).
export default function ServiceZonesCard() {
  const [zones, setZones] = useState([]);
  const load = () => api.get("/service-zones").then((r) => setZones(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const setField = (i, k, v) => setZones(zones.map((z, idx) => (idx === i ? { ...z, [k]: v } : z)));

  const saveZone = async (z) => {
    const payload = {
      name: z.name || "", cities: z.cities || [],
      postal_prefixes: (typeof z.postal_prefixes === "string" ? z.postal_prefixes.split(",") : (z.postal_prefixes || [])),
      install_rate: Number(z.install_rate) || 0, onsite_fee: Number(z.onsite_fee) || 0, local_delivery_fee: Number(z.local_delivery_fee) || 0,
    };
    try {
      if (z.id) await api.put(`/service-zones/${z.id}`, payload);
      else await api.post("/service-zones", payload);
      toast.success(`Zone "${payload.name}" saved`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const del = async (z) => {
    if (!z.id) { setZones(zones.filter((x) => x !== z)); return; }
    try { await api.delete(`/service-zones/${z.id}`); toast.success("Zone deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const add = () => setZones([...zones, { name: "New Zone", postal_prefixes: "", install_rate: 65, onsite_fee: 45, local_delivery_fee: 0 }]);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="service-zones-card">
      <div className="flex items-center justify-between mb-1 gap-3">
        <h3 className="font-head font-bold">Service &amp; Delivery Zones</h3>
        <Button data-testid="zone-add" onClick={add} variant="outline" className="rounded-sm h-8 text-xs"><Plus size={14} className="mr-1" />Add zone</Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Each area (detected by postal code) sets its <b>Installation $/hr/person</b>, <b>Onsite estimate fee</b>, and <b>Local delivery fee</b>. Postal prefixes are the first 3 characters (FSA), comma-separated (e.g. V5C, V5H).</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase text-slate-400 text-left">
            <th className="py-1 pr-2">Zone</th><th className="py-1 pr-2">Postal prefixes (FSA)</th>
            <th className="py-1 pr-2">Install $/hr</th><th className="py-1 pr-2">Onsite $</th><th className="py-1 pr-2">Local delivery $</th><th /></tr></thead>
          <tbody>
            {zones.map((z, i) => (
              <tr key={z.id || i} data-testid="zone-row" className="border-t border-slate-100">
                <td className="py-1 pr-2"><Input data-testid="zone-name" value={z.name || ""} onChange={(e) => setField(i, "name", e.target.value)} className="rounded-sm h-8 w-32" /></td>
                <td className="py-1 pr-2"><Input data-testid="zone-prefixes" value={Array.isArray(z.postal_prefixes) ? z.postal_prefixes.join(", ") : (z.postal_prefixes || "")} onChange={(e) => setField(i, "postal_prefixes", e.target.value)} className="rounded-sm h-8 w-56 uppercase text-xs" /></td>
                <td className="py-1 pr-2"><Input data-testid="zone-install" type="number" step="0.5" value={z.install_rate} onChange={(e) => setField(i, "install_rate", e.target.value)} className="rounded-sm h-8 w-20 num text-right" /></td>
                <td className="py-1 pr-2"><Input data-testid="zone-onsite" type="number" step="0.5" value={z.onsite_fee} onChange={(e) => setField(i, "onsite_fee", e.target.value)} className="rounded-sm h-8 w-20 num text-right" /></td>
                <td className="py-1 pr-2"><Input data-testid="zone-delivery" type="number" step="0.5" value={z.local_delivery_fee} onChange={(e) => setField(i, "local_delivery_fee", e.target.value)} className="rounded-sm h-8 w-20 num text-right" /></td>
                <td className="py-1 whitespace-nowrap text-right">
                  <button data-testid="zone-save" onClick={() => saveZone(z)} className="p-1.5 text-[#2495D3] hover:text-[#1E7AA9]" title="Save"><Save size={15} /></button>
                  <button data-testid="zone-delete" onClick={() => del(z)} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {zones.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400 text-xs">No zones yet — add one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
